import type { UseQueryResult } from '@tanstack/react-query'
import { MESSAGES } from '~/components/Charts/chrome/chartChrome'
import { usePageControls, usePageCharts, useKpisForPage } from '~/api/hooks/useCharts'
import { deriveDefaultEncodedKpi } from '~/state/deriveDefaultEncodedKpi'
import type { ChartRegistryRow } from '~/api/endpoints/getCharts'
import type { ReviewPageState } from './useReviewPageState'
import type { ComparisonState } from './useComparisonState'

export interface CompareKpiResolution {
  /** The encoded compare KPI in metric mode; the primary kpi otherwise. */
  compareEncodedKpi: string
  /** False while metric mode still waits for the controls' kpi_metadata (a bare root wouldn't encode/match). */
  compareReady: boolean
  /** The compare KPI's OWN page registry rows — metric mode resolves fact/walk against these. */
  compareCharts: UseQueryResult<ChartRegistryRow[], Error>
  /** The controls query — metric mode must surface its error loudly (no silently-disabled compare). */
  controls: ReturnType<typeof usePageControls>
}

/** Resolves the compared KPI for metric-mode compare, shared by ComparisonChartTile (both
 *  overlays) and DynamicsPanelsTile. Encodes the compared root against the PAGE's allowlist
 *  (mirrors useKpiCascadeState.filteredItems), so deriveDefaultEncodedKpi can't pick a
 *  (driver,value_type) the page has no chart row for. All hooks are unconditional. */
export function useCompareKpiResolution(
  state: ReviewPageState,
  compare: ComparisonState,
): CompareKpiResolution {
  const controls = usePageControls(state.factTable)
  const pageKpis = useKpisForPage(state.pageId)
  const allItems = controls.data?.kpi_metadata?.items ?? []
  // Undefined (still loading) -> pass through unfiltered to avoid flicker.
  const items =
    pageKpis.data === undefined
      ? allItems
      : allItems.filter((m) => (pageKpis.data as string[]).includes(m.kpi_id))
  const compareEncodedKpi =
    compare.mode === 'metric' ? deriveDefaultEncodedKpi(compare.compareKpi, items) : state.kpi
  const compareReady = compare.mode !== 'metric' || items.length > 0
  // Only METRIC mode needs the compare KPI's own registry rows; period/off mode reuses the
  // PRIMARY's rows, which the page already holds — consumers consult compareCharts exclusively
  // on metric paths, so the disabled query (data: undefined) is never read otherwise.
  const compareCharts = usePageCharts(
    state.pageId,
    compare.mode === 'metric' ? compareEncodedKpi : null,
  )
  return { compareEncodedKpi, compareReady, compareCharts, controls }
}

/** Shared label for the comparison series/row. */
export function comparisonLabelFor(state: ReviewPageState, compare: ComparisonState): string {
  if (compare.mode === 'metric') {
    return (
      state.kpiCascade.rootOptions.find((o) => o.value === compare.compareKpi)?.label ??
      // Unreachable in practice: compareKpi is always chosen FROM rootOptions
      // (ComparisonSwitch lists otherRoots), so the find can only miss while options are
      // still loading. Kept as a render-safe raw-token fallback, not a designed code path.
      compare.compareKpi
    )
  }
  return MESSAGES.previousPeriod
}
